import { useCallback, useEffect, useState } from 'react';
import type {
  PaigeExportPage,
  PaigeExportQuery,
  PaigeExportRow,
  PaigeExportsChannel,
} from './export-types';

/** Rows a page: what fits the sheet without a long scroll (CapturePoint's Activity shows 40). */
export const PAGE = 40;

export type Filters = Required<Pick<PaigeExportQuery, 'q' | 'type' | 'day'>> & {
  state: PaigeExportQuery['state'] | '';
};

export const noFilters: Filters = { q: '', type: '', state: '', day: '' };

/** The query the API reads for these filters at this cursor. */
export function queryOf(filters: Filters, cursor: string | undefined): PaigeExportQuery {
  const { state, ...rest } = filters;
  return { ...rest, ...(state ? { state } : {}), ...(cursor ? { cursor } : {}), limit: PAGE };
}

/** What a read answered, under the key (filters, cursor, generation) it was read for. */
type Answer = { key: string; page?: PaigeExportPage; error?: string };

/**
 * The job's export history a page at a time, newest first, read on the server with the filters as they were when
 * Find was pressed: `older` follows the page's cursor and `newer` goes back along the cursors already seen, so a
 * million deliveries cost the same as forty. `act` runs Retry or Cancel on one row and reads the page again.
 */
export function useExportHistory(channel: PaigeExportsChannel | undefined) {
  const [applied, setApplied] = useState<Filters>(noFilters);
  const [cursors, setCursors] = useState<string[]>([]);
  const [generation, setGeneration] = useState(0);
  const cursor = cursors.at(-1);
  const key = JSON.stringify([applied, cursor, generation]);
  const answer = useAnswer(channel, key, queryOf(applied, cursor));
  const current = answer?.key === key ? answer : null;
  const next = current?.page?.nextCursor ?? null;
  const find = useCallback((filters: Filters) => (setApplied(filters), setCursors([])), []);
  const reload = () => setGeneration((g) => g + 1);
  const act = async (action: 'retry' | 'cancel', row: PaigeExportRow) => {
    if (!channel) return;
    await channel[action](row.id);
    reload();
  };
  return { ...pagerOf(cursors, next, setCursors), ...readOf(current), applied, find, reload, act };
}

/** The page's rows (null while it is read) and the error, from the answer for the current key. */
function readOf(current: Answer | null) {
  return { rows: current?.page?.exports ?? null, error: current?.error ?? null };
}

/** Older follows the page's cursor; Newer goes back one; the page number for people. */
function pagerOf(
  cursors: string[],
  next: string | null,
  set: (update: (seen: string[]) => string[]) => void,
) {
  return {
    older: next ? () => set((seen) => [...seen, next]) : null,
    newer: cursors.length > 0 ? () => set((seen) => seen.slice(0, -1)) : null,
    page: cursors.length + 1,
  };
}

/** The latest answer of the channel, read again whenever the key changes. */
function useAnswer(channel: PaigeExportsChannel | undefined, key: string, query: PaigeExportQuery) {
  const [answer, setAnswer] = useState<Answer | null>(null);
  useEffect(() => {
    if (!channel) return;
    let live = true;
    channel
      .history(JSON.parse(JSON.stringify(query)) as PaigeExportQuery)
      .then((page) => live && setAnswer({ key, page }))
      .catch(
        (failure) =>
          live &&
          setAnswer({
            key,
            error: failure instanceof Error ? failure.message : 'Paige could not read the history',
          }),
      );
    return () => {
      live = false;
    };
    // The key stands for the query: a new object every render is the same query until the key moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, key]);
  return answer;
}
